<template>
  <div class="project-wayline-wrapper height-100">
    <a-spin :spinning="loading" :delay="300" tip="downloading" size="large">
    <div style="height: 50px; line-height: 50px; border-bottom: 1px solid #4f4f4f; font-weight: 450;">
      <a-row>
        <a-col :span="1"></a-col>
        <a-col :span="15">Flight Route Library</a-col>
        <a-col :span="8" v-if="importVisible" class="flex-row flex-justify-end flex-align-center">
          <a-upload
            name="file"
            :multiple="false"
            :before-upload="beforeUpload"
            :show-upload-list="false"
            :customRequest="uploadFile"
          >
            <a-button type="text" style="color: white;">
              <SelectOutlined />
            </a-button>
          </a-upload>
        </a-col>
      </a-row>
    </div>
    <div :style="{ height : height + 'px'}" class="scrollbar">
      <div id="data" class="height-100 uranus-scrollbar" v-if="waylinesData.data.length !== 0" @scroll="onScroll">
        <div v-for="wayline in waylinesData.data" :key="wayline.id">
          <div class="wayline-panel"
            :style="{
              paddingTop: '5px',
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: selectedWayline[wayline.id] ?? '#3c3c3c'
            }"
            @click="selectRoute(wayline)">
            <div class="title">
              <a-tooltip :title="wayline.name">
                <div class="pr10" style="width: 120px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.name }}</div>
              </a-tooltip>
              <div class="ml10"><UserOutlined /></div>
              <a-tooltip :title="wayline.user_name">
                <div class="ml5 pr10" style="width: 80px; white-space: nowrap; text-overflow: ellipsis; overflow: hidden;">{{ wayline.user_name }}</div>
              </a-tooltip>
              <div class="fz20">
                <a-dropdown>
                  <a style="color: white;">
                    <EllipsisOutlined />
                  </a>
                  <template #overlay>
                    <a-menu theme="dark" class="more" style="background: #3c3c3c;">
                      <a-menu-item @click="downloadWayline(wayline.id, wayline.name)">
                        <span>Download</span>
                      </a-menu-item>
                      <a-menu-item @click="showWaylineTip(wayline.id)">
                        <span>Delete</span>
                      </a-menu-item>
                      <a-menu-item @click="readWaylineFile(wayline.id, wayline.name)">
                        <span>Load</span>
                      </a-menu-item>
                      <a-menu-item @click="removeWaylinePolygon(wayline.id)">
                        <span>Clear</span>
                      </a-menu-item>
                    </a-menu>
                  </template>
                </a-dropdown>
              </div>
            </div>
            <div class="ml10 mt5" style="color: hsla(0,0%,100%,0.65);">
              <span><RocketOutlined /></span>
              <span class="ml5">{{ DEVICE_NAME[wayline.drone_model_key] }}</span>
              <span class="ml10"><CameraFilled style="border-top: 1px solid; padding-top: -3px;" /></span>
              <span class="ml5" v-for="payload in wayline.payload_model_keys" :key="payload.id">
                {{ DEVICE_NAME[payload] }}
              </span>
              <span class="ml10">
                <EyeInvisibleOutlined
                  v-if="selectedWayline[wayline.id]"
                  @click="removeWaylinePolygon(wayline.id)"
                />
                <EyeOutlined
                  v-else
                  @click="readWaylineFile(wayline.id)"
                />
              </span>
            </div>
            <div class="mt5 ml10" style="color: hsla(0,0%,100%,0.35);">
              <span class="mr10">Update at {{ new Date(wayline.update_time).toLocaleString() }}</span>
            </div>
          </div>
        </div>
      </div>
      <div v-else>
        <a-empty :image-style="{ height: '60px', marginTop: '60px' }" />
      </div>
      <a-modal v-model:visible="deleteTip" width="450px" :closable="false" :maskClosable="false" centered :okButtonProps="{ danger: true }" @ok="deleteWayline">
          <p class="pt10 pl20" style="height: 50px;">Wayline file is unrecoverable once deleted. Continue?</p>
          <template #title>
              <div class="flex-row flex-justify-center">
                  <span>Delete</span>
              </div>
          </template>
      </a-modal>
    </div>
    </a-spin>
  </div>
</template>

<script lang="ts" setup>
import { reactive } from '@vue/reactivity'
import { message } from 'ant-design-vue'
import { computed, onMounted, onUpdated, ref } from 'vue'
import { deleteWaylineFile, downloadWaylineFile, getWaylineBoundaryCoordinates, getWaylineFiles, importKmzFile } from '/@/api/wayline'
import { ELocalStorageKey, ERouterName } from '/@/types'
import { EllipsisOutlined, RocketOutlined, CameraFilled, UserOutlined, SelectOutlined, EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons-vue'
import { DEVICE_NAME } from '/@/types/device'
import { useMyStore } from '/@/store'
import { WaylineFile } from '/@/types/wayline'
import { downloadFile } from '/@/utils/common'
import { IPage } from '/@/api/http/type'
import { CURRENT_CONFIG } from '/@/api/http/config'
import { load } from '@amap/amap-jsapi-loader'
import { getRoot } from '/@/root'
import { fromLonLat } from 'ol/proj'
import { LineString, Point, Polygon } from 'ol/geom'
import Feature from 'ol/Feature'
import VectorSource from 'ol/source/Vector'
import VectorLayer from 'ol/layer/Vector'
import { Fill, Icon, Stroke, Style, Text } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import { Coordinate } from 'ol/coordinate'
import arrowIcon from '/@/assets/icons/arrow-up-circle.svg'
import { StyleLike } from 'ol/style/Style'
import { asArray, asString } from 'ol/color'

const loading = ref(false)
const store = useMyStore()
const pagination :IPage = {
  page: 1,
  total: -1,
  page_size: 10
}

const waylinesData = reactive({
  data: [] as WaylineFile[]
})

const root = getRoot()
const workspaceId = localStorage.getItem(ELocalStorageKey.WorkspaceId)!
const deleteTip = ref(false)
const deleteWaylineId = ref<string>('')
const canRefresh = ref(true)
const importVisible = ref<boolean>(root.$router.currentRoute.value.name === ERouterName.WAYLINE)
const height = ref()

let waylineVectorSource = null as any
const predefinedColors = ['#FF0000', '#00FF00', '#0000FF', '#FFFF00'] // previous fixed colors
let hueCounter = 0
let colorIndex = 0
// const selectedWayline = reactive<{
//     [id: string]:string}>({})
const selectedWayline = computed({
  get: () => store.state.selectedWayline,
  set: (val) => {
    return val
  }
})

onMounted(() => {
  const parent = document.getElementsByClassName('scrollbar').item(0)?.parentNode as HTMLDivElement
  height.value = document.body.clientHeight - parent.firstElementChild!.clientHeight
  getWaylines()

  const key = setInterval(() => {
    const data = document.getElementById('data')?.lastElementChild as HTMLDivElement
    if (pagination.total === 0 || Math.ceil(pagination.total / pagination.page_size) <= pagination.page || height.value <= data?.clientHeight + data?.offsetTop) {
      clearInterval(key)
      return
    }
    pagination.page++
    getWaylines()
  }, 1000)
})

function getWaylines () {
  if (!canRefresh.value) {
    return
  }
  canRefresh.value = false
  getWaylineFiles(workspaceId, {
    page: pagination.page,
    page_size: pagination.page_size,
    order_by: 'update_time desc'
  }).then(res => {
    if (res.code !== 0) {
      return
    }
    waylinesData.data = [...waylinesData.data, ...res.data.list]
    pagination.total = res.data.pagination.total
    pagination.page = res.data.pagination.page
  }).finally(() => {
    canRefresh.value = true
  })
}

function showWaylineTip (waylineId: string) {
  deleteWaylineId.value = waylineId
  deleteTip.value = true
}

function darkenColor (color: string | number[], factor: number = 0.2): string {
  const rgba = asArray(color)
  const r = Math.round(rgba[0] * (1 - factor))
  const g = Math.round(rgba[1] * (1 - factor))
  const b = Math.round(rgba[2] * (1 - factor))
  return asString([r, g, b, rgba[3] ?? 1])
}

function deleteWayline () {
  deleteWaylineFile(workspaceId, deleteWaylineId.value).then(res => {
    if (res.code === 0) {
      message.success('Wayline file deleted')
    }
    deleteWaylineId.value = ''
    deleteTip.value = false
    pagination.total = 0
    pagination.page = 1
    waylinesData.data = []
    getWaylines()
  })
}

function downloadWayline (waylineId: string, fileName: string) {
  loading.value = true
  downloadWaylineFile(workspaceId, waylineId).then(res => {
    if (!res) {
      return
    }
    const data = new Blob([res], { type: 'application/zip' })
    downloadFile(data, fileName + '.kmz')
  }).finally(() => {
    loading.value = false
  })
}

function readWaylineFile (waylineId: string) {
  if (selectedWayline.value[waylineId]) return
  loading.value = true
  getWaylineBoundaryCoordinates(workspaceId, waylineId).then(res => {
    if (!res) {
      return
    }

    const projectedBoundaryCoords = res.boundary.map((c:any) => fromLonLat([c.lon, c.lat]))

    // 🟦 Create Polygon geometry
    const polygon = new Polygon([projectedBoundaryCoords])

    // Create a feature and style
    const color:string = getRandomColor()

    const boundaryFeature = new Feature({
      geometry: polygon
    })

    boundaryFeature.setStyle(
      new Style({
        stroke: new Stroke({ color, width: 2 }),
        fill: new Fill({ color: color.startsWith('hsl') ? color.replace(')', ',0.3)') : `${color}33` })
      })
    )
    boundaryFeature.setId(waylineId + '-boundary')

    const projectedWaylineCoords = res.waypoints.map((c:any) => fromLonLat([c.lon, c.lat]))
    // window.console.log(projectedWaylineCoords)
    const waylineFeature = new Feature({
      geometry: new LineString(projectedWaylineCoords)
    })

    waylineFeature.setStyle(
      new Style({
        stroke: new Stroke({
          color: color.startsWith('hsl') ? color.replace(')', ',0.5)') : `${color}50`,
          width: 2
        })
      }),
    )
    waylineFeature.setId(waylineId + '-wayline')

    // --- 3️⃣ Create Point features ---
    const pointFeatures = res.waypoints.map((wp: any, index: number) => {
      const coord = fromLonLat([wp.lon, wp.lat])

      const feature = new Feature({
        geometry: new Point(coord),
        // Store your data so we can access it in the style function
        wp: wp, // important!
        color: color, // if color can change per waypoint
      })

      feature.setId(`${waylineId}-point-${index}`)
      return feature
    })

    // ==================== STYLE FUNCTION ====================
    const createPointStyle = (feature: Feature, resolution: number) => {
      const wp = feature.get('wp') as any
      const baseColor = feature.get('color') || '#000000'
      const darkenedColor = darkenColor(baseColor, 0.40)

      // === Dynamic font size based on resolution ===
      const baseSize = 16
      let fontSize = Math.round(baseSize / Math.max(resolution * 2, 0.8)) // ← tune this multiplier

      // Optional: clamp between min and max size
      fontSize = Math.max(10, Math.min(36, fontSize))
      // const fontSize = Math.round(8 / resolution)

      const showText = resolution < 1.2

      return new Style({
        text: showText
          ? new Text({
            // text: (wp.height !== undefined ? String(Math.round(wp.height)) : '') + ', ' + (wp.angle !== undefined ? String(Math.round(wp.angle)) : ''),
            text: String(wp.index) + '(' + (wp.height !== undefined ? String(Math.round(wp.height)) : '') + ')',
            font: `${fontSize}px sans-serif`,
            overflow: false,
            fill: new Fill({ color: darkenedColor }), // .startsWith('hsl') ? color.replace(')', ',0.5)') : `${color}50` }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
            offsetY: -8,
          })
          : undefined,
        image: new Icon({
          src: arrowIcon,
          anchor: [0.5, 0.5],
          color: baseColor,
          scale: 0.2,
          rotateWithView: true,
          rotation: (wp.angle * Math.PI) / 180,
        }),
      })
    }

    if (!waylineVectorSource) {
      waylineVectorSource = new VectorSource({
        features: [boundaryFeature, waylineFeature, ...pointFeatures]
      })
      waylineVectorSource.set('id', 'waylineVectorSource')
      // Create vector layer
      const vectorLayer = new VectorLayer({
        source: waylineVectorSource,
        style: createPointStyle,
      })
      root.$map.addLayer(vectorLayer)
    } else {
      // waylineVectorSource.addFeature(boundaryFeature)
      // waylineVectorSource.addFeature(waylineFeature)
      // pointFeatures.forEach((f) => waylineVectorSource.addFeature(f))
      waylineVectorSource.addFeatures([boundaryFeature, waylineFeature, ...pointFeatures])
    }

    selectedWayline.value[waylineId] = color
    // store.commit('SET_SELECTED_WAYLINE', { waylineId, color })
  }).finally(() => {
    loading.value = false
  })
}

function removeWaylinePolygon (waylineId: string) {
  if (!selectedWayline.value[waylineId]) return
  if (!waylineVectorSource) {
    const layers = root.$map.getLayers().getArray()
    const layer = layers.find(l => l.getSource()?.get('id') === 'waylineVectorSource')
    waylineVectorSource = layer?.getSource()
  }
  waylineVectorSource.getFeatures().forEach((f) => {
    if (f.getId()?.startsWith(waylineId)) {
      waylineVectorSource.removeFeature(f)
    }
  })
  delete selectedWayline.value[waylineId]
}

function getRandomColor (): string {
  // return colors[Math.floor(Math.random() * colors.length)]
  // Use predefined colors first
  if (colorIndex < predefinedColors.length) {
    return predefinedColors[colorIndex++]
  }
  // Once predefined are exhausted, use harmonic HSL colors
  const hue = hueCounter % 360
  hueCounter += 60 // adjust step for harmony
  return `hsl(${hue}, 70%, 50%)`
}

function onWaylineCheckedChange (event: Event, waylineId: string) {
  const target = event.target as HTMLInputElement
  if (!target.checked) {
    removeWaylinePolygon(waylineId)
  } else {
    readWaylineFile(waylineId)
  }
}

function selectRoute (wayline: WaylineFile) {
  // window.console.log('select Route:', wayline)
  store?.commit('SET_SELECT_WAYLINE_INFO', wayline)
  // readWaylineFile(wayline.id)
}

function onScroll (e: any) {
  const element = e.srcElement
  if (element.scrollTop + element.clientHeight >= element.scrollHeight - 5 && Math.ceil(pagination.total / pagination.page_size) > pagination.page && canRefresh.value) {
    pagination.page++
    getWaylines()
  }
}

interface FileItem {
  uid: string;
  name?: string;
  status?: string;
  response?: string;
  url?: string;
}

interface FileInfo {
  file: FileItem;
  fileList: FileItem[];
}
const fileList = ref<FileItem[]>([])

function beforeUpload (file: FileItem) {
  fileList.value = [file]
  loading.value = true
  return true
}
const uploadFile = async () => {
  fileList.value.forEach(async (file: FileItem) => {
    const fileData = new FormData()
    fileData.append('file', file, file.name)
    await importKmzFile(workspaceId, fileData).then((res) => {
      if (res.code === 0) {
        message.success(`${file.name} file uploaded successfully`)
        canRefresh.value = true
        pagination.total = 0
        pagination.page = 1
        waylinesData.data = []
        getWaylines()
      }
    }).finally(() => {
      loading.value = false
      fileList.value = []
    })
  })
}

</script>

<style lang="scss" scoped>
.wayline-panel {
  background: #3c3c3c;
  margin-left: auto;
  margin-right: auto;
  margin-top: 10px;
  height: 90px;
  width: 95%;
  font-size: 13px;
  border-radius: 2px;
  cursor: pointer;
  .title {
    display: flex;
    flex-direction: row;
    align-items: center;
    height: 30px;
    font-weight: bold;
    margin: 0px 10px 0 10px;
  }
}
.uranus-scrollbar {
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: #c5c8cc transparent;
}
</style>
